import { useEffect, useMemo } from 'react';

import { useFieldArray, useFormContext } from 'react-hook-form';
import { FaTrash } from 'react-icons/fa';
import styled from 'styled-components';

import {
    TotemState,
    GetDetailedTotemsOfOrganizationQuery,
    GetEquipmentsQuery,
    useReactivateTotemMutation,
    useArchiveTotemMutation,
} from 'data/__generated__';

import { LOCATION_COLUMN_LABELS } from 'pages/Locations';

import {
    DetailFormCheckbox,
    DetailFormSelect,
    DetailFormTextArea,
    DetailFormValue,
    DetailLink,
    DetailValue,
    StateHistory,
} from 'components/DetailsView';
import { ColumnsSectionContainer, SectionColumn, SectionContainer } from 'components/DetailsView/Section';
import { TotemLabel } from 'components/TotemLabel';
import { StatusTag } from './StatusTag';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { Option } from 'components/TotemSelect';
import { CopyValue } from 'components/CopyValue';
import { AdresseInfoForm } from './AdresseInfoForm';
import { EquipmentSelector } from 'pages/Organizations/OrganizationDetails/TotemsDetails/EquipmentSelector';
import { colors } from 'constants/colors';

export type TotemFormData = {
    name: string;
    address: string;
    zipcode: string;
    city: string;
    country: string;
    latitude: number;
    longitude: number;
    address_details: string;
    consigneDelivery: string;
    accessTime: number;
    equipments: {
        equipmentId: string;
        quantity: number;
        comment: string;
    }[];
    mainUserId: string;
    minimumOrderAmountInfo: {
        minimumBakeryAmount: number;
        minimumDryAmount: number;
        minimumFreshAmount: number;
        minimumCatererAmount: number;
        minimumFruitAmount: number;
        minimumTotalAmount: number;
    };
    needSignature: boolean;
};

// Renders the totem form sections. The parent page owns the form (`useForm` + `FormProvider`) and the save action
// (in the page header, like OrganizationGlobalForm); this component consumes the form via `useFormContext`.
export const TotemInfo = ({
    totem,
    mainUserOptions,
    equipments,
}: {
    totem: GetDetailedTotemsOfOrganizationQuery['totemsOfOrganization'][number];
    mainUserOptions: Option<string>[];
    equipments: GetEquipmentsQuery['adminEquipments'];
}) => {
    const [archiveTotem] = useArchiveTotemMutation();
    const [reactivateTotem] = useReactivateTotemMutation();

    const { setValue, control, register, watch } = useFormContext<TotemFormData>();

    const {
        _id: totemId,
        accessTime,
        address,
        address_details,
        city,
        zipcode,
        consigneDelivery,
        country,
        equipments: totemEquipments,
        latitude,
        longitude,
        mainUser,
        minimumOrderAmountInfo,
        name,
        needSignature,
        state,
        stateHistory,
    } = totem;

    const formattedStateHistory = stateHistory.map((stateRecord) => ({
        ...stateRecord,
        state: <StatusTag state={stateRecord.state} />,
    }));

    const {
        fields: equipmentFields,
        append,
        remove,
    } = useFieldArray({
        control,
        name: 'equipments' as const,
    });

    useEffect(() => {
        if (totemEquipments) {
            const totemEquipmentFields = totemEquipments.map(({ equipmentId, quantity, comment }) => ({
                equipmentId,
                quantity,
                comment: comment ?? '',
            }));
            setValue('equipments', totemEquipmentFields);
        }

        // Initialisation des valeurs d'adresse
        setValue('address', address ?? '');
        setValue('city', city ?? '');
        setValue('zipcode', zipcode ?? '');
        setValue('country', country ?? '');
        setValue('latitude', latitude ?? 0);
        setValue('longitude', longitude ?? 0);
        setValue('needSignature', needSignature ?? false);
        setValue('mainUserId', mainUser?._id ?? '');
    }, [totemEquipments, setValue, city, zipcode, country, latitude, longitude, address, needSignature, mainUser?._id]);

    const mainUserSelectOptions = useMemo(() => {
        if (!mainUser || mainUserOptions.some(({ value }) => value === mainUser._id)) {
            return mainUserOptions;
        }
        return [...mainUserOptions, { value: mainUser._id, label: `${mainUser.fullName} (non-admin)` }];
    }, [mainUser, mainUserOptions]);

    const isArchived = state === TotemState.Archived;

    return (
        <>
            <SectionContainer title="Informations générales" isInitiallyOpen>
                <ColumnsSectionContainer numberOfColumns={2}>
                    <SectionColumn style={{ position: 'relative' }}>
                        <CopyValue value={totemId} />
                        <DetailFormValue defaultValue={name} {...register('name')} label="Nom du totem" />
                        <DetailFormSelect<string>
                            label="Utilisateur principal"
                            placeholder="Sélectionner un admin"
                            defaultValue={mainUser?._id}
                            name="mainUserId"
                            options={mainUserSelectOptions}
                        />
                        {mainUser ? (
                            <DetailLink
                                path="user"
                                name={`Plus d'infos sur ${mainUser.fullName}`}
                                value={mainUser._id}
                            />
                        ) : null}
                        <StatusTag state={state} showHeading />
                        <DetailFormCheckbox
                            name="needSignature"
                            label="Signature à la livraison"
                            defaultChecked={needSignature}
                        />
                        <DetailFormValue
                            defaultValue={accessTime}
                            type="number"
                            step="1"
                            {...register('accessTime', { required: true, valueAsNumber: true })}
                            label="Temps d'accès"
                        />
                    </SectionColumn>
                    <SectionColumn>
                        <div>
                            <TotemLabel>Adresse</TotemLabel>
                            <AddressReadout>
                                {watch('address')}
                                <br />
                                {watch('zipcode')} {watch('city')} {watch('country')}
                                <br />
                                <br />
                                Lat: {watch('latitude')}, Long: {watch('longitude')}
                            </AddressReadout>
                        </div>
                        <AdresseInfoForm totem={totem} />
                        <DetailFormTextArea
                            defaultValue={address_details}
                            {...register('address_details', {
                                required: true,
                            })}
                            label="Complément d'adresse"
                        />
                        <DetailFormTextArea
                            defaultValue={consigneDelivery}
                            {...register('consigneDelivery', {
                                required: true,
                            })}
                            label="Instructions de livraison"
                        />
                    </SectionColumn>
                </ColumnsSectionContainer>
            </SectionContainer>

            <SectionContainer title="Montants minimaux de commande" isInitiallyOpen>
                <ColumnsSectionContainer numberOfColumns={3}>
                    <DetailFormValue
                        defaultValue={minimumOrderAmountInfo.minimumTotalAmount}
                        type="number"
                        step="1"
                        {...register('minimumOrderAmountInfo.minimumTotalAmount', {
                            required: true,
                            valueAsNumber: true,
                        })}
                        label="Total"
                    />
                    <DetailFormValue
                        defaultValue={minimumOrderAmountInfo.minimumBakeryAmount}
                        type="number"
                        step="1"
                        {...register('minimumOrderAmountInfo.minimumBakeryAmount', {
                            required: true,
                            valueAsNumber: true,
                        })}
                        label={`Flux ${LOCATION_COLUMN_LABELS.bakery}`}
                    />
                    <DetailFormValue
                        defaultValue={minimumOrderAmountInfo.minimumDryAmount}
                        type="number"
                        step="1"
                        {...register('minimumOrderAmountInfo.minimumDryAmount', {
                            required: true,
                            valueAsNumber: true,
                        })}
                        label={`Flux ${LOCATION_COLUMN_LABELS.dry}`}
                    />
                    <DetailFormValue
                        defaultValue={minimumOrderAmountInfo.minimumFreshAmount}
                        type="number"
                        step="1"
                        {...register('minimumOrderAmountInfo.minimumFreshAmount', {
                            required: true,
                            valueAsNumber: true,
                        })}
                        label={`Flux ${LOCATION_COLUMN_LABELS.fresh}`}
                    />
                    <DetailFormValue
                        defaultValue={minimumOrderAmountInfo.minimumCatererAmount}
                        type="number"
                        step="1"
                        {...register('minimumOrderAmountInfo.minimumCatererAmount', {
                            required: true,
                            valueAsNumber: true,
                        })}
                        label={`Flux ${LOCATION_COLUMN_LABELS.caterer}`}
                    />
                    <DetailFormValue
                        defaultValue={minimumOrderAmountInfo.minimumFruitAmount}
                        type="number"
                        step="1"
                        {...register('minimumOrderAmountInfo.minimumFruitAmount', {
                            required: true,
                            valueAsNumber: true,
                        })}
                        label={`Flux ${LOCATION_COLUMN_LABELS.fruit}`}
                    />
                </ColumnsSectionContainer>
            </SectionContainer>

            <SectionContainer title="Équipements" isInitiallyOpen>
                <EquipmentsSection>
                    {equipmentFields?.length ? (
                        <EquipmentsGrid>
                            {equipmentFields.map((item, index) => {
                                const equipment = equipments.find(({ _id }) => _id === item.equipmentId);
                                const equipmentName = equipment?.fullname || item.equipmentId;
                                return (
                                    <ItemContainer key={item.equipmentId}>
                                        <DetailValue value={equipmentName} label="Nom" />
                                        <DetailFormValue
                                            defaultValue={item.quantity}
                                            label="Quantité"
                                            type="number"
                                            step="1"
                                            min="1"
                                            {...register(`equipments.${index}.quantity`, {
                                                required: true,
                                                valueAsNumber: true,
                                            })}
                                            style={{ marginTop: 10 }}
                                        />
                                        <DetailFormTextArea
                                            label="Commentaires"
                                            defaultValue={item.comment}
                                            height="100px"
                                            style={{ marginTop: 10 }}
                                            {...register(`equipments.${index}.comment`)}
                                        />
                                        <IconContainer onClick={() => remove(index)}>
                                            <TrashIcon size={16} color={colors.pureWhite} />
                                        </IconContainer>
                                    </ItemContainer>
                                );
                            })}
                        </EquipmentsGrid>
                    ) : (
                        <EmptyLabel>Aucun équipement</EmptyLabel>
                    )}
                    <AddContainer>
                        <EquipmentSelector equipmentFields={equipmentFields} equipments={equipments} append={append} />
                    </AddContainer>
                </EquipmentsSection>
            </SectionContainer>

            <SectionContainer title="Historique des statuts" isInitiallyOpen={false}>
                <StatusSection>
                    <StateHistory records={formattedStateHistory} />
                    <SectionActions>
                        {isArchived ? (
                            <TotemPrimaryButton
                                type="button"
                                onClick={() => reactivateTotem({ variables: { totemId } })}
                            >
                                Réactiver le totem
                            </TotemPrimaryButton>
                        ) : (
                            <TotemPrimaryButton type="button" onClick={() => archiveTotem({ variables: { totemId } })}>
                                Archiver le totem
                            </TotemPrimaryButton>
                        )}
                    </SectionActions>
                </StatusSection>
            </SectionContainer>
        </>
    );
};

const AddressReadout = styled.div`
    margin-top: 5px;
    color: ${({ theme }) => theme.textColor};
`;

const EquipmentsSection = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;

    & > :not(:first-child) {
        margin-top: 15px;
    }
`;

const EquipmentsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 15px;
    width: 100%;
`;

const EmptyLabel = styled.div`
    color: ${({ theme }) => theme.infoTextColor};
`;

const ItemContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 10px;
    border-radius: ${({ theme }) => theme.borderRadius};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
`;

const AddContainer = styled.div`
    display: flex;
    width: 100%;
    max-width: 400px;
    border-radius: ${({ theme }) => theme.borderRadius};
`;

const StatusSection = styled.div`
    display: flex;
    flex-direction: column;

    & > :not(:first-child) {
        margin-top: 15px;
    }
`;

const SectionActions = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const TrashIcon = styled(FaTrash)`
    color: ${({ theme }) => theme.menuTextColor};
`;

const IconContainer = styled.div`
    position: absolute;
    top: 5px;
    right: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 40px;
    background-color: ${({ theme }) => theme.ctaPrimaryColor};
    cursor: pointer;
`;
